import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import {
  ArrowLeft,
  Star,
  Award,
  Users,
  Calendar,
  Quote,
  Dumbbell,
  Heart,
  Target
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function MeetCoach() {
  const [coachInfo, setCoachInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoachInfo();
  }, []);

  const fetchCoachInfo = async () => {
    try {
      const response = await axios.get(`${API}/coach`);
      setCoachInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch coach info:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">MEET YOUR TRAINER</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-end">
          <div className="absolute inset-0">
            <img 
              src={coachInfo?.images?.hero} 
              alt="Coach Coop"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="text-sm text-primary uppercase tracking-widest mb-2">{coachInfo?.title}</div>
              <h1 className="text-5xl sm:text-6xl font-black uppercase tracking-tighter mb-4">{coachInfo?.name}</h1>
              <div className="flex flex-wrap gap-3">
                {coachInfo?.specialties?.map((specialty, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 bg-primary/20 border border-primary/50 rounded-sm text-sm uppercase tracking-wider"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-card border-y border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <div className="text-4xl font-black text-primary font-mono">{coachInfo?.stats?.clients_transformed}+</div>
                <div className="text-sm text-muted-foreground uppercase">Clients Transformed</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <Calendar className="h-8 w-8 text-accent mx-auto mb-2" />
                <div className="text-4xl font-black text-accent font-mono">{coachInfo?.stats?.years_experience}</div>
                <div className="text-sm text-muted-foreground uppercase">Years Experience</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <Dumbbell className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <div className="text-4xl font-black text-orange-500 font-mono">{coachInfo?.stats?.programs_created}</div>
                <div className="text-sm text-muted-foreground uppercase">Programs Created</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <Target className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <div className="text-4xl font-black text-blue-500 font-mono">{coachInfo?.stats?.success_rate}%</div>
                <div className="text-sm text-muted-foreground uppercase">Success Rate</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bio */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-black uppercase tracking-tighter mb-6">THE STORY</h2>
              <div className="prose prose-invert max-w-none">
                {coachInfo?.bio?.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-muted-foreground text-lg leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Action Photos */}
        <section className="py-12 bg-card">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-6">IN ACTION</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {coachInfo?.images?.action?.map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <img 
                    src={img} 
                    alt={`Coach Coop action ${i + 1}`}
                    className="w-full h-64 object-cover rounded-sm border border-border"
                  />
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <img 
                  src={coachInfo?.images?.lifestyle} 
                  alt="Coach Coop lifestyle"
                  className="w-full h-64 object-cover rounded-sm border border-border"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Quotes */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-8">WORDS TO LIVE BY</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {coachInfo?.quotes?.map((quote, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="bg-secondary/30 border-border h-full">
                    <CardContent className="p-6">
                      <Quote className="h-6 w-6 text-primary mb-3" />
                      <p className="text-lg font-medium italic">"{quote}"</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-12 bg-card border-t border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-6">CERTIFICATIONS</h2>
            <div className="flex flex-wrap gap-4">
              {coachInfo?.certifications?.map((cert, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/30 rounded-sm"
                >
                  <Award className="h-5 w-5 text-accent" />
                  <span className="font-medium">{cert}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
